import { Schema } from "effect";
// Input schema for creating a webhook endpoint
export const WebhookEndpointCreate = Schema.Struct({
    url: Schema.String.pipe(Schema.nonEmptyString(), Schema.annotations({
        description: "The URL where webhook events will be sent",
        example: "https://example.com/webhook"
    })),
    description: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "A description of the webhook endpoint",
        example: "Production webhook for user events"
    }))),
    filterTypes: Schema.optional(Schema.Array(Schema.String).pipe(Schema.annotations({
        description: "Array of event types to filter for this endpoint",
        example: [
            "user.created",
            "user.updated"
        ]
    }))),
    headers: Schema.optional(Schema.Record({
        key: Schema.String,
        value: Schema.String
    }).pipe(Schema.annotations({
        description: "Custom headers to include with webhook requests",
        example: {
            "X-API-Key": "secret-key"
        }
    }))),
    metadata: Schema.optional(Schema.Record({
        key: Schema.String,
        value: Schema.String
    }).pipe(Schema.annotations({
        description: "Metadata to include with webhook requests",
        example: {
            name: "Webhook Name",
            description: "Webhook Description"
        }
    })))
});
// Output schema for webhook endpoint responses
export const WebhookEndpoint = Schema.Struct({
    id: Schema.String.pipe(Schema.annotations({
        description: "Unique identifier for the webhook endpoint",
        example: "ep_2kj3h4k5j6h7k8j9"
    })),
    url: Schema.String.pipe(Schema.annotations({
        description: "The URL where webhook events are sent",
        example: "https://example.com/webhook"
    })),
    description: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "Description of the webhook endpoint",
        example: "Production webhook for user events"
    })),
    version: Schema.Number.pipe(Schema.annotations({
        description: "API version for the webhook",
        example: 1
    })),
    disabled: Schema.Boolean.pipe(Schema.annotations({
        description: "Whether the webhook endpoint is disabled",
        example: false
    })),
    filterTypes: Schema.NullOr(Schema.Array(Schema.String)).pipe(Schema.annotations({
        description: "Event types this endpoint is subscribed to",
        example: [
            "user.created",
            "user.updated"
        ]
    })),
    channels: Schema.NullOr(Schema.Array(Schema.String)).pipe(Schema.annotations({
        description: "Channels this endpoint is subscribed to",
        example: [
            "user-events"
        ]
    })),
    createdAt: Schema.Union(Schema.String, Schema.Date).pipe(Schema.annotations({
        description: "ISO 8601 timestamp of when the endpoint was created",
        example: "2024-01-15T10:30:00Z"
    })),
    updatedAt: Schema.Union(Schema.String, Schema.Date).pipe(Schema.annotations({
        description: "ISO 8601 timestamp of when the endpoint was last updated",
        example: "2024-01-15T10:30:00Z"
    })),
    headers: Schema.optional(Schema.Record({
        key: Schema.String,
        value: Schema.String
    }).pipe(Schema.annotations({
        description: "Custom headers configured for this endpoint",
        example: {
            "X-API-Key": "secret-key"
        }
    }))),
    secret: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "The secret for the webhook endpoint",
        example: "secret-key"
    })))
});
// List response schema with pagination
export const WebhookEndpointList = Schema.Struct({
    data: Schema.Array(WebhookEndpoint),
    done: Schema.Boolean.pipe(Schema.annotations({
        description: "Whether there are more results to fetch",
        example: false
    })),
    iterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator for fetching the next page of results",
        example: "ep_2kj3h4k5j6h7k8j9"
    }))),
    prevIterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator for fetching the previous page of results",
        example: "ep_1kj3h4k5j6h7k8j8"
    })))
});
// Schema for updating webhook headers
export const WebhookHeadersUpdate = Schema.Struct({
    headers: Schema.Record({
        key: Schema.String,
        value: Schema.String
    }).pipe(Schema.annotations({
        description: "Headers to set for the webhook endpoint",
        example: {
            "X-API-Key": "new-secret-key",
            "X-Custom-Header": "value"
        }
    }))
});
// Schema for updating webhook endpoint
export const WebhookEndpointUpdate = Schema.Struct({
    url: Schema.String,
    description: Schema.optional(Schema.String),
    filterTypes: Schema.optional(Schema.Array(Schema.String)),
    disabled: Schema.Boolean,
    metadata: Schema.optional(Schema.Record({
        key: Schema.String,
        value: Schema.String
    })),
    headers: Schema.optional(Schema.Record({
        key: Schema.String,
        value: Schema.String
    }))
});
// URL parameters for listing webhooks
export const WebhookListParams = Schema.Struct({
    limit: Schema.optional(Schema.NumberFromString.pipe(Schema.annotations({
        description: "Maximum number of results to return",
        example: "20"
    }))),
    iterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator from previous response for pagination",
        example: "ep_2kj3h4k5j6h7k8j9"
    })))
});
// URL parameters for listing webhook messages
export const WebhookMessageListParams = Schema.Struct({
    limit: Schema.optional(Schema.NumberFromString.pipe(Schema.annotations({
        description: "Maximum number of results to return",
        example: "20"
    }))),
    iterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator from previous response for pagination",
        example: "msg_2kj3h4k5j6h7k8j9"
    }))),
    channel: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Filter by channel",
        example: "project-123"
    }))),
    before: Schema.optional(Schema.DateFromString.pipe(Schema.annotations({
        description: "Filter messages before this date",
        example: "2024-01-15T10:30:00Z"
    }))),
    after: Schema.optional(Schema.DateFromString.pipe(Schema.annotations({
        description: "Filter messages after this date",
        example: "2024-01-15T10:30:00Z"
    }))),
    withContent: Schema.optional(Schema.BooleanFromString.pipe(Schema.annotations({
        description: "Include message content in response",
        example: "true"
    }))),
    tag: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Filter by tag",
        example: "user-events"
    }))),
    eventTypes: Schema.optional(Schema.Array(Schema.String).pipe(Schema.annotations({
        description: "Filter by event types",
        example: [
            "payment.created",
            "payment.updated"
        ]
    })))
});
// Schema for individual webhook message
export const WebhookMessage = Schema.Struct({
    id: Schema.String.pipe(Schema.annotations({
        description: "Unique identifier for the webhook message",
        example: "msg_2kj3h4k5j6h7k8j9"
    })),
    eventType: Schema.String.pipe(Schema.annotations({
        description: "Type of the event",
        example: "payment.created"
    })),
    eventId: Schema.NullishOr(Schema.String.pipe(Schema.annotations({
        description: "Unique identifier for the event",
        example: "evt_2kj3h4k5j6h7k8j9"
    }))),
    channels: Schema.NullishOr(Schema.Array(Schema.String).pipe(Schema.annotations({
        description: "Channels this message was sent to",
        example: [
            "project-123"
        ]
    }))),
    payload: Schema.Record({
        key: Schema.String,
        value: Schema.Unknown
    }).pipe(Schema.annotations({
        description: "The payload data of the webhook message",
        example: {
            projectId: 123,
            object: "event",
            timestamp: 1640995200000
        }
    })),
    timestamp: Schema.Union(Schema.String, Schema.Date).pipe(Schema.annotations({
        description: "ISO 8601 timestamp of when the message was created",
        example: "2024-01-15T10:30:00Z"
    }))
});
// List response schema for webhook messages with pagination
export const WebhookMessageList = Schema.Struct({
    data: Schema.Array(WebhookMessage),
    done: Schema.Boolean.pipe(Schema.annotations({
        description: "Whether there are more results to fetch",
        example: false
    })),
    iterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator for fetching the next page of results",
        example: "msg_2kj3h4k5j6h7k8j9"
    }))),
    prevIterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator for fetching the previous page of results",
        example: "msg_1kj3h4k5j6h7k8j8"
    })))
});
// Schema for individual message attempt
export const WebhookMessageAttempt = Schema.Struct({
    id: Schema.String.pipe(Schema.annotations({
        description: "Unique identifier for the message attempt",
        example: "atmpt_2kj3h4k5j6h7k8j9"
    })),
    endpointId: Schema.String.pipe(Schema.annotations({
        description: "ID of the endpoint this attempt was made to",
        example: "ep_2kj3h4k5j6h7k8j9"
    })),
    msgId: Schema.String.pipe(Schema.annotations({
        description: "ID of the message this attempt is for",
        example: "msg_2kj3h4k5j6h7k8j9"
    })),
    status: Schema.Number.pipe(Schema.annotations({
        description: "Status of the attempt (0=Success, 1=Pending, 2=Fail, 3=Sending)",
        example: 0
    })),
    response: Schema.String.pipe(Schema.annotations({
        description: "Response from the webhook endpoint",
        example: "OK"
    })),
    responseStatusCode: Schema.Number.pipe(Schema.annotations({
        description: "HTTP status code from the webhook endpoint response",
        example: 200
    })),
    responseDurationMs: Schema.Number.pipe(Schema.annotations({
        description: "Response time in milliseconds",
        example: 150
    })),
    timestamp: Schema.Union(Schema.String, Schema.Date).pipe(Schema.annotations({
        description: "ISO 8601 timestamp of when the attempt was made",
        example: "2024-01-15T10:30:00Z"
    })),
    triggerType: Schema.Number.pipe(Schema.annotations({
        description: "Type of trigger that caused this attempt",
        example: 1
    })),
    url: Schema.String.pipe(Schema.annotations({
        description: "The URL the request was sent to",
        example: "https://example.com/webhook"
    })),
    msg: Schema.optional(WebhookMessage.pipe(Schema.annotations({
        description: "Full message object if requested with withMsg=true"
    })))
});
// URL parameters for listing message attempts by endpoint
export const WebhookMessageAttemptListParams = Schema.Struct({
    limit: Schema.optional(Schema.NumberFromString.pipe(Schema.annotations({
        description: "Maximum number of results to return",
        example: "20"
    }))),
    iterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator from previous response for pagination",
        example: "atmpt_2kj3h4k5j6h7k8j9"
    }))),
    status: Schema.optional(Schema.NumberFromString.pipe(Schema.annotations({
        description: "Filter by attempt status (0=Success, 1=Pending, 2=Fail, 3=Sending)",
        example: "0"
    }))),
    statusCodeClass: Schema.optional(Schema.NumberFromString.pipe(Schema.annotations({
        description: "Filter by HTTP status code class (1=1xx, 2=2xx, 3=3xx, 4=4xx, 5=5xx)",
        example: "2"
    }))),
    channel: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Filter by channel",
        example: "project-123"
    }))),
    tag: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Filter by tag",
        example: "user-events"
    }))),
    before: Schema.optional(Schema.DateFromString.pipe(Schema.annotations({
        description: "Filter attempts before this date",
        example: "2024-01-15T10:30:00Z"
    }))),
    after: Schema.optional(Schema.DateFromString.pipe(Schema.annotations({
        description: "Filter attempts after this date",
        example: "2024-01-15T10:30:00Z"
    }))),
    withContent: Schema.optional(Schema.BooleanFromString.pipe(Schema.annotations({
        description: "Include message content in response",
        example: "true"
    }))),
    withMsg: Schema.optional(Schema.BooleanFromString.pipe(Schema.annotations({
        description: "Include full message object in response",
        example: "true"
    }))),
    eventTypes: Schema.optional(Schema.Array(Schema.String).pipe(Schema.annotations({
        description: "Filter by event types",
        example: [
            "payment.created",
            "payment.updated"
        ]
    })))
});
// List response schema for message attempts with pagination
export const WebhookMessageAttemptList = Schema.Struct({
    data: Schema.Array(WebhookMessageAttempt),
    done: Schema.Boolean.pipe(Schema.annotations({
        description: "Whether there are more results to fetch",
        example: false
    })),
    iterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator for fetching the next page of results",
        example: "atmpt_2kj3h4k5j6h7k8j9"
    }))),
    prevIterator: Schema.optional(Schema.String.pipe(Schema.annotations({
        description: "Iterator for fetching the previous page of results",
        example: "atmpt_1kj3h4k5j6h7k8j8"
    })))
});
// URL parameters for getting endpoint stats
export const WebhookEndpointStatsParams = Schema.Struct({
    since: Schema.optional(Schema.DateFromString.pipe(Schema.annotations({
        description: "Start date for stats calculation (inclusive)",
        example: "2024-01-15T10:30:00Z"
    }))),
    until: Schema.optional(Schema.DateFromString.pipe(Schema.annotations({
        description: "End date for stats calculation (inclusive)",
        example: "2024-01-20T10:30:00Z"
    })))
});
// Schema for endpoint statistics response
export const WebhookEndpointStats = Schema.Struct({
    success: Schema.Number.pipe(Schema.annotations({
        description: "Number of successful webhook deliveries",
        example: 150
    })),
    fail: Schema.Number.pipe(Schema.annotations({
        description: "Number of failed webhook deliveries",
        example: 5
    })),
    pending: Schema.Number.pipe(Schema.annotations({
        description: "Number of webhook deliveries that are pending/queued",
        example: 2
    })),
    sending: Schema.Number.pipe(Schema.annotations({
        description: "Number of webhook deliveries currently being sent",
        example: 1
    }))
});
